import React, { useEffect, useRef, useState } from 'react';
import { Users, Zap, Award, ThumbsUp, Star, TrendingUp } from 'lucide-react';

const useCountUp = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
};

const StatCard = ({ icon: Icon, value, suffix, label, color, delay }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const count = useCountUp(value, 2000, visible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="flex flex-col items-center p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-100 hover:border-transparent transition-all duration-500 hover:scale-105 cursor-default"
      style={{
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        animationDelay: `${delay}ms`,
        ...(visible ? { boxShadow: `0 8px 30px ${color}30` } : {}),
      }}
    >
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
        style={{ background: `linear-gradient(135deg, ${color}20, ${color}40)` }}
      >
        <Icon className="w-7 h-7" style={{ color }} />
      </div>
      <div className="text-3xl font-black mb-1" style={{ color }}>
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-gray-500 text-center font-medium">{label}</div>
    </div>
  );
};

const SocialSignals = () => {
  const stats = [
    { icon: Users, value: 2500, suffix: '+', label: 'Happy Customers', color: '#2CC84B' },
    { icon: Zap, value: 15, suffix: ' MW+', label: 'Capacity Installed', color: '#00BCD4' },
    { icon: Award, value: 9, suffix: '+', label: 'Years Experience', color: '#3F51B5' },
    { icon: ThumbsUp, value: 98, suffix: '%', label: 'Customer Satisfaction', color: '#2CC84B' },
    { icon: Star, value: 4.9, suffix: '★', label: 'Average Rating', color: '#F59E0B' },
    { icon: TrendingUp, value: 500, suffix: '+', label: 'Projects This Year', color: '#00BCD4' },
  ];

  return (
    <section className="py-16 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 50%, #eef2ff 100%)' }}>
      {/* Decorative bg elements */}
      <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #2CC84B, #00BCD4, #3F51B5)' }} />
      <div className="absolute bottom-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #3F51B5, #00BCD4, #2CC84B)' }} />

      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-sm font-semibold" style={{ background: 'linear-gradient(90deg, #2CC84B20, #00BCD420)', color: '#2CC84B', border: '1px solid #2CC84B40' }}>
            <Star className="w-4 h-4 fill-current" />
            Trusted by Thousands Across India
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
            Our Impact in{' '}
            <span style={{ background: 'linear-gradient(90deg, #2CC84B, #00BCD4, #3F51B5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Numbers
            </span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Join thousands of satisfied customers who switched to solar with Ekta Power
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
          {stats.map((stat, i) => (
            <StatCard key={i} {...stat} delay={i * 100} />
          ))}
        </div>

        {/* Recent Activity Feed */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-center text-sm font-semibold text-gray-400 uppercase tracking-wider mb-6">Live Installations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { name: 'Rajesh M.', location: 'Pune', size: '5 kW', time: '2 days ago', savings: '₹3,200/mo' },
              { name: 'Priya K.', location: 'Mumbai', size: '10 kW', time: '1 week ago', savings: '₹6,800/mo' },
              { name: 'Suresh P.', location: 'Nashik', size: '3 kW', time: '3 days ago', savings: '₹2,100/mo' },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100 hover:border-[#2CC84B]/30 transition-all duration-300"
                style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #2CC84B, #00BCD4)' }}>
                  {item.name[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{item.name}, {item.location}</p>
                  <p className="text-xs text-gray-500">{item.size} installed · {item.time}</p>
                  <p className="text-xs font-semibold mt-1" style={{ color: '#2CC84B' }}>Saving {item.savings}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialSignals;
